/**
 * CompanyHome.jsx – regenerated 29-Jul-2025
 * ---------------------------------------------------------------
 * Stand-alone dashboard with FULL CRUD for users, leases, wells, tanks,
 * audit logs, **and production/well-test charts** (new “Charts” tab).
 * Route: /companyHome/:subdomain
 * ---------------------------------------------------------------
 */
import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTheme, useUser } from "ogcommon";
import UsersTab from "./UsersTab";
import ExplorerModal from "./ExplorerModal";
import ScheduledReportsPanel from "./ScheduledReports"; // ← NEW
import LeasesTab from "./LeasesTab";
import PurchasersTab from "./PurchasersTab";
import H10SelfInitPanel from "./H10SelfInitPanel";

export default function CompanyHome() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { userRole, userID, setUser, companyName } = useUser(); // no subdomain from context
  const { subdomain } = useParams(); // ← now only source for subdomain
  const [jobTypes, setJobTypes] = useState([]);
  const CompanyLogs = React.lazy(() => import("./CompanyLogs"));

  /* ───────────── top-level state ───────────── */
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [company, setCompany] = useState(null);
  const [activeTab, setActiveTab] = useState("logs"); // default tab

  /* slices editable in-page */
  const [users, setUsers] = useState([]);
  const [leases, setLeases] = useState([]);
  const [purchasers, setPurchasers] = useState([]);
  const [tags, setTags] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [showExplorer, setShowExplorer] = useState(false); // ⬅️ new

  /* filters */
  const [userFilter, setUserFilter] = useState("");
  const [leaseFilter, setLeaseFilter] = useState("");
  const [useFieldTicket, setUseFieldTicket] = useState("");

  const [showActiveLeases, setShowActiveLeases] = useState(true);
  const [hasOgFiles, setOgFiles] = useState(false);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const tokenAccess = searchParams.has("token"); // or check for a specific param/key

  const srcHint = (searchParams.get("src") || "").toLowerCase();
  const fromOgFieldTicket = (() => {
    if (typeof window === "undefined") return false;
    const params = new URLSearchParams(window.location.search);
    const src = (params.get("src") || "").toLowerCase();
    return src === "fieldticket" || src === "ogfieldticket";
  })();

  /* ───────────── fetch payload once ───────────── */
  const fetchCompany = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const r = await fetch(
        `https://house.ogendeavors.com/api/clientDetails.php?company=${encodeURIComponent(
          subdomain || "",
        )}`,
      );
      const js = await r.json();
      if (!Array.isArray(js) || !js.length) throw new Error("Client not found");
      const single = js[0];

      // parse perms JSON (hasOgFiles, UseFieldTicket)
      let perms = {};
      if (typeof single.perms === "string") {
        try {
          perms = JSON.parse(single.perms);
        } catch (e) {
          console.warn("Invalid perms JSON", e);
        }
      } else if (single.perms && typeof single.perms === "object") {
        perms = single.perms;
      }

      setCompany(single);
      setOgFiles(!!perms.hasOgFiles);
      setUseFieldTicket(!!perms.UseFieldTicket);
      setJobTypes(Array.isArray(single.jobTypes) ? single.jobTypes : []);
      setUsers(Array.isArray(single.users) ? single.users : []);
      setLeases(Array.isArray(single.leases) ? single.leases : []);
      setPurchasers(Array.isArray(single.purchasers) ? single.purchasers : []);
      setTags(Array.isArray(single.tags) ? single.tags : []);
      setAuditLog(Array.isArray(single.auditLog) ? single.auditLog : []);
    } catch (err) {
      setError(err.message || "Unknown error");
    } finally {
      setLoading(false);
    }
  }, [subdomain]);

  useEffect(() => {
    if (company && auditLog.length === 0) {
      setActiveTab("leases"); // or "users"
    }
  }, [company, auditLog]);

  useEffect(() => {
    setShowExplorer(activeTab === "explorer");
  }, [activeTab]);

  const baseTabs = [
    { id: "leases", label: `Leases (${leases.length})` },
    { id: "users", label: `Users (${users.length})` },
    { id: "purchasers", label: `Purchasers (${purchasers.length})` },
    { id: "logs", label: "Audit Logs" }, // ← always present
    { id: "schedules", label: "Scheduled Reports" }, // ← NEW Tab
  ];

  // Per-tenant H-10 self-setup is gated by clients.EnableH10SelfInit on the
  // master clients row. Tab + panel only appear when the flag is 'Y'.
  const h10SelfInitEnabled = company && company.EnableH10SelfInit === "Y";

  // only add “Explorer” if hasOgFiles is true
  let tabs = hasOgFiles
    ? [...baseTabs, { id: "explorer", label: "Explorer" }]
    : baseTabs;
  if (h10SelfInitEnabled) {
    tabs = [...tabs, { id: "h10_self_init", label: "H-10 Well Setup" }];
  }

  useEffect(() => {
    fetchCompany();
  }, [fetchCompany]);

  /* ───────────── derived lists ───────────── */
  const filteredUsers = useMemo(() => {
    const q = userFilter.toLowerCase();
    return users.filter(
      (u) =>
        u.UserID.toLowerCase().includes(q) ||
        (u.FullName || "").toLowerCase().includes(q) ||
        (u.Email || "").toLowerCase().includes(q),
    );
  }, [users, userFilter]);

  const filteredLeases = useMemo(() => {
    const q = leaseFilter.toLowerCase();
    return leases
      .filter((l) => (showActiveLeases ? l.Active === "Y" : l.Active !== "Y"))
      .filter(
        (l) =>
          l.LeaseID.toLowerCase().includes(q) ||
          (l.LeaseName || "").toLowerCase().includes(q) ||
          (l.RRC || "").toLowerCase().includes(q),
      );
  }, [leases, leaseFilter, showActiveLeases]);

  /* ───────────── theme helpers ───────────── */
  const clsCard =
    theme === "dark"
      ? "bg-gray-800 text-gray-100 border-gray-700"
      : "bg-white text-gray-800 border-gray-200";

  const clsInput =
    theme === "dark"
      ? "bg-gray-700 text-gray-100 border-gray-600"
      : "bg-white text-gray-800 border-gray-300";

  /* ───────────── loading / error guards ───────────── */
  if (loading)
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500" />
      </div>
    );
  if (error)
    return (
      <div className="flex h-screen items-center justify-center">
        <p className="text-red-600 text-lg font-semibold">{error}</p>
      </div>
    );
  if (!company) return null;

  /* ───────────── render ───────────── */
  return (
    <div
      className={`min-h-screen transition-colors duration-300 ${
        theme === "dark"
          ? "bg-gray-900 text-gray-100"
          : "bg-gray-100 text-gray-900"
      }`}
    >
      <div className="container mx-auto px-4 py-6 max-w-6xl">
        {/* header */}
        <div
          className={`p-6 mb-6 rounded-xl shadow-md border ${clsCard} bg-gradient-to-r from-blue-500 to-indigo-500 bg-opacity-80`}
        >
          <h1 className="text-3xl font-bold text-white mb-1">
            {company.company}
          </h1>

          <p className="text-white/80">
            {tokenAccess ? (
              fromOgFieldTicket ? (
                <a
                  href={`https://${company.subdomain}.ogfieldticket.ogpumper.net`}
                  target="_top"
                  rel="noopener"
                  className="underline font-mono"
                >
                  Return to ogFieldTicket
                </a>
              ) : (
                <a
                  href={`https://${company.subdomain}.${company.AppName}.com`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline font-mono"
                >
                  Return to ogPumper
                </a>
              )
            ) : (
              <>
                <a
                  href={`https://${company.subdomain}.${company.AppName}.com`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline font-mono"
                >
                  {company.subdomain}.{company.AppName}.com
                </a>
                <p className="mt-1">
                  Status:{" "}
                  <span
                    className={
                      company.Active === "Y" ? "text-green-300" : "text-red-300"
                    }
                  >
                    {company.Active === "Y" ? "Active" : "Inactive"}
                  </span>
                </p>
              </>
            )}
          </p>
        </div>

        {/* tabs */}
        <div className="mb-4 flex flex-wrap gap-2 border-b pb-2">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`px-4 py-2 rounded-t-lg font-semibold transition-colors ${
                activeTab === t.id
                  ? "border-b-2 border-blue-500 text-blue-700"
                  : theme === "dark"
                    ? "text-gray-400 hover:text-gray-200"
                    : "text-gray-500 hover:text-gray-800"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* ───── Users ───── */}
        {activeTab === "users" && (
          <UsersTab
            users={filteredUsers}
            setUsers={setUsers}
            theme={theme}
            companyName={subdomain}
            userFilter={userFilter}
            setUserFilter={setUserFilter}
            clsInput={clsInput}
            clsCard={clsCard}
            role={userRole.role}
            useFieldTicket={useFieldTicket ? "Y" : "N"} // ← use instantiated perms
            jobTypes={jobTypes}
          />
        )}

        {/* ───── Leases ───── */}
        {activeTab === "leases" && (
          <LeasesTab
            leases={leases}
            setLeases={setLeases}
            users={users}
            purchasers={purchasers}
            tags={tags}
            theme={theme}
            companyName={subdomain}
            searchTerm={leaseFilter}
            setSearchTerm={setLeaseFilter}
            showActive={showActiveLeases}
            setShowActive={setShowActiveLeases}
            clsCard={clsCard}
          />
        )}

        {/* ───── Purchasers ───── */}
        {activeTab === "purchasers" && (
          <PurchasersTab
            purchasers={purchasers}
            setPurchasers={setPurchasers}
            theme={theme}
            companyName={subdomain}
            role={userRole?.role || "A"}
            baseUrl="https://house.ogendeavors.com"
          />
        )}

        {/* ───── Logs ───── */}
        {activeTab === "logs" && (
          <React.Suspense
            fallback={
              <div className="flex h-64 items-center justify-center">
                <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-500 border-solid"></div>
              </div>
            }
          >
            <CompanyLogs
              theme={theme}
              company={subdomain}
              apiBase="https://house.ogendeavors.com/api"
              pageSize={10000}
              auditSince=""
              auditSlim={false}
            />
          </React.Suspense>
        )}

        {/* ───── Schedules (NEW) ───── */}
        {activeTab === "schedules" && (
          <ScheduledReportsPanel
            subdomain={subdomain}
            users={users}
            leases={leases}
            tags={tags}
            theme={theme}
            baseUrl="https://house.ogendeavors.com/api"
          />
        )}

        {/* ───── H-10 Self-Init (gated by clients.EnableH10SelfInit) ───── */}
        {h10SelfInitEnabled && activeTab === "h10_self_init" && (
          <H10SelfInitPanel
            subdomain={subdomain}
            theme={theme}
            companyName={company.company}
          />
        )}

        {hasOgFiles && (
          <ExplorerModal
            open={showExplorer} // ✅ tells modal to render
            theme={theme} // ✅ for dark/light styles
            companyName={company.company} // just a label in the header
            onClose={() => setActiveTab("logs")}
          />
        )}
      </div>
    </div>
  );
}

/* ───────────────────────────────────────────────────────────── */
/*  INPUT CLASS HELPER                                          */
/* ───────────────────────────────────────────────────────────── */
const inputClasses = (theme, { readOnly = false } = {}) =>
  `w-full px-2 py-1 rounded border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
    readOnly ? "opacity-70 cursor-not-allowed" : ""
  } ${
    theme === "dark"
      ? "bg-gray-700 text-gray-100 border-gray-600"
      : "bg-white text-gray-800 border-gray-300"
  }`;

/* ───────────────────────────────────────────────────────────── */
/*  USERS TAB (with full Create / Edit modal)                   */
/* ───────────────────────────────────────────────────────────── */

